import React from 'react';
import { Box, Typography, Chip, Alert, List, ListItem, ListItemText } from '@mui/material';
import { CheckCircle, Build, ReportProblem, ErrorOutline } from '@mui/icons-material';

// Affiche le résultat du gate de validation (génération ou vérification déterministe).
// validation : { status?, isValid?, defects?, deterministic?, semantic?, proposed? }

const STATUS_META = {
  valid: { label: 'Valide', color: 'success', icon: <CheckCircle fontSize="small" /> },
  repaired: { label: 'Réparé (déterministe)', color: 'info', icon: <Build fontSize="small" /> },
  semantic_issues: { label: 'Défauts sémantiques restants', color: 'warning', icon: <ReportProblem fontSize="small" /> },
  invalid: { label: 'Invalide', color: 'error', icon: <ErrorOutline fontSize="small" /> },
};

const KIND_LABEL = {
  structure: 'Structure',
  alignment: 'Alignement notion',
  shortcut: 'Anti-raccourci',
  semantic: 'Sémantique (LLM)',
};

const severityColor = (s) => (s === 'high' ? 'error' : s === 'medium' ? 'warning' : 'default');

function resolveStatus(validation) {
  if (validation?.status) return validation.status;
  return validation?.isValid ? 'valid' : 'invalid';
}

function resolveDefects(validation) {
  if (Array.isArray(validation?.defects)) return validation.defects;
  return [...(validation?.deterministic || []), ...(validation?.semantic || [])];
}

const ExerciseValidationPanel = ({ validation }) => {
  if (!validation) return null;

  const status = resolveStatus(validation);
  const meta = STATUS_META[status] || STATUS_META.invalid;
  const defects = resolveDefects(validation);

  return (
    <Box sx={{ mb: 2 }}>
      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 1 }}>
        <Chip icon={meta.icon} label={meta.label} color={meta.color} size="small" />
        {validation.proposed && (
          <Chip label="Correctif sémantique proposé" color="info" variant="outlined" size="small" />
        )}
        <Typography variant="caption" color="text.secondary">
          {defects.length === 0 ? 'Aucun défaut détecté' : `${defects.length} défaut${defects.length > 1 ? 's' : ''}`}
        </Typography>
      </Box>

      {defects.length === 0 ? (
        <Alert severity="success" variant="outlined">Cet exercice passe le contrôle déterministe.</Alert>
      ) : (
        <List dense disablePadding>
          {defects.map((d, i) => (
            <ListItem key={i} sx={{ alignItems: 'flex-start', py: 0.25 }}>
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                    <Chip label={KIND_LABEL[d.kind] || d.kind || 'défaut'} size="small" variant="outlined" />
                    <Chip label={d.severity || 'high'} size="small" color={severityColor(d.severity)} />
                    {Number.isInteger(d.itemIndex) && (
                      <Chip label={`item #${d.itemIndex}`} size="small" variant="outlined" />
                    )}
                  </Box>
                }
                secondary={d.description}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

export default ExerciseValidationPanel;
